import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@mui/styles";
import { ImageList } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { Button, Grid, Typography, Grow, Zoom, IconButton, Tooltip } from "@mui/material";
import { withStyles } from "@mui/styles";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import AddIcon from "@mui/icons-material/Add";

import { getFavorites, getFavoritesToLayout } from "../store/ducks/duckFavorites";

import FavoriteForm from "./FavoriteForm";
import FavoriteElement from "./FavoriteElement";

const StyledIconButton = withStyles({
  root: {
    borderRadius: "5%",
  },
})(IconButton);

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    maxWidth: "100%",
    flex: "1 0 0",
    backgroundColor: theme.palette.background.default,
  },
  gridList: {
    flexWrap: "nowrap",
    // Promote the list into his own layer on Chrome. This cost memory but helps keeping high FPS.
    transform: "translateZ(0)",
    "&::-webkit-scrollbar": {
      width: 16,
      height: 12,
      borderRadius: 5,
      margin: 10,
    },
    "&::-webkit-scrollbar-track": {
      boxShadow: "inset 0 0 6px rgba(0,0,0,0.00)",
      webkitBoxShadow: "inset 0 0 6px rgba(0,0,0,0.025)",
      borderRadius: 5,
      backgroundColor: "rgba(0,0,0,.01)",
      margin: 10,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(0,0,0,.05)",
      outline: "transparent",
      marginTop: 10,
      backgroundClip: "padding-box",
      //   marginBottom: 10,
      borderRadius: 5,
    },
    WebkitOverflowScrolling: "touch",
    width: "100%",
  },
  title: {
    paddingLeft: 20,
    width: 175,
    marginRight: 0,
    marginLeft: 0,
  },
  titleBar: {
    background:
      "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)",
  },
  skeleton: {
    padding: 40,
  },
  paper: {
    height: 165,
    margin: 7,
    width: 197,
  },
  linkText: {
    maxWidth: "100%",
    display: "block",
    color: "#006d31",
    paddingRight: 50,
  },
  textItem: {
    //color: 'rgba(0, 0, 0, 0.54)',
    color: "rgba(37, 36, 35, 0.5)",
    "&:hover": {
      background: "white",
      color: "rgba(37, 36, 35, 1)",
      fontSize: theme.typography.pxToRem(17),
      boxShadow: "1px 1px 9px 1px #8888885e",
    },
  },
  star: {
    color: "rgb(218, 197, 0)",
  },
}));

export default function FavoritesContainer() {
  const classes = useStyles();
  const paths = useSelector((state) => state.sidebar.paths);
  const tileRef = useRef(null);
  const [end, setEnd] = useState(false);
  const { loading, data } = useSelector((state) => state.favorites);
  const isMinLength = useSelector((state) => state.favorites.data.length > 4);
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const handleOpen = () => setShow(true);

  useEffect(() => {
    dispatch(getFavorites());
    dispatch(getFavoritesToLayout());
  }, [dispatch]);

  const ButtonScroll = withStyles({
    root: {
      borderRadius: 0,
      minWidth: 40,
      width: 40,
      height: 40,
    },
  })(Button);

  const handleClickRight = (e) => {
    e.preventDefault();
    if (tileRef?.current) {
      tileRef.current.scrollLeft = tileRef.current.scrollWidth;
      setEnd(true);
    }
  };
  const handleClickLeft = (e) => {
    e.preventDefault();
    if (tileRef?.current) {
      tileRef.current.scrollLeft = 0;
      setEnd(false);
    }
  };

  const getLayoutName = (url) => {
    let items = url.split("/").filter((x) => x);

    return items[items.length - 1];
  };

  return (
    <>
      <Grid container alignItems="center" direction="row" justify="space-between">
        <Grid item xs={2}>
          <Typography color="textSecondary" variant="subtitle1">
            Favorites
          </Typography>
        </Grid>
        <Grid item xs={8} />
        <Grid item style={{ marginRight: -10 }} xs={1}>
          {isMinLength && (
            <>
              <ButtonScroll
                disabled={!end}
                style={{ width: 30 }}
                variant="text"
                onClick={handleClickLeft}
              >
                <ChevronLeftIcon />
              </ButtonScroll>
              <ButtonScroll
                disabled={end}
                style={{ width: 30 }}
                variant="text"
                onClick={handleClickRight}
              >
                <ChevronRightIcon />
              </ButtonScroll>
            </>
          )}
        </Grid>
        <Grid item style={{ paddingLeft: 25, paddingRight: 0 }} xs={1}>
          <Tooltip title="Add Favorite">
            <StyledIconButton size="medium" onClick={handleOpen}>
              <AddIcon />
            </StyledIconButton>
          </Tooltip>
        </Grid>
      </Grid>
      <Grid item style={{ minHeight: 200 }} xs={12}>
        <div className={classes.root}>
          <ImageList ref={tileRef} className={classes.gridList} cols={4} spacing={0}>
            <Grow unmountOnExit in={!loading && data.length === 0}>
              <FavoriteElement key={"Favorite-Element-Skeleton"} isSkeleton id={"loading"} />
            </Grow>

            {!loading &&
              paths &&
              data.map((layout, i) => {
                let { id: favoriteId, layoutId: id } = layout;

                if (!paths[id]) return null; // podría darse el caso dónde no tenga acceso el usuario

                let name = getLayoutName(paths[id]);
                let pathComplete = paths[id];

                return (
                  <Zoom key={`Favorite-element-zoom-${i}`} timeout={{ appear: 500 * 1 }}>
                    <FavoriteElement
                      key={`Favorite-Element-${i}-${id}`}
                      favoriteId={favoriteId}
                      id={id}
                      name={name}
                      pathComplete={pathComplete}
                    />
                  </Zoom>
                );
              })}
            {/* <Zoom timeout={{appear: 3000,enter: 3000}} style={{transitionDelay: '500ms'}} in={!loading}>

               <AddFavorite/> 
            </Zoom> */}
          </ImageList>
        </div>
      </Grid>
      <FavoriteForm handleClose={handleClose} open={show} />
    </>
  );
}
